<template>
  <div class="container-fluid py-4">

    <!-- ================= LOADER GLOBAL ================= -->
    <div v-if="isPageLoading" class="overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
    </div>

    <!-- ================= HEADER ================= -->
    <div
      v-if="!isPageLoading"
      class="d-flex justify-content-between align-items-center mb-4 header-medical"
    >

      <div class="d-flex align-items-center gap-3">

        <i class="bi bi-hospital fs-2 text-primary"></i>

        <div>
          <h2 class="fw-bold mb-0">
            Modifier Configuration Système Médical
          </h2>

          <small class="text-header">
            Mise à jour des paramètres de gestion des matériels médicaux
          </small>
        </div>

      </div>

      <router-link
        class="btn btn-outline-primary btn-lg shadow-sm btn-medical"
        :to="{ name: 'item_config' }"
      >
        <i class="bi bi-arrow-left-circle me-2"></i>
        Retour
      </router-link>

    </div>

    <!-- ================= CARD ================= -->
    <div
      v-if="!isPageLoading"
      class="card-medical p-4 shadow-sm rounded"
    >

      <!-- SECTION TITLE -->
      <div class="section-title mb-4">
        <i class="bi bi-gear-wide-connected"></i>
        <span>Paramètres du système médical</span>
      </div>

      <!-- FORM -->
      <div class="row g-4">

        <!-- NOM -->
        <div class="col-md-6">

          <label class="form-label fw-semibold">Nom du système</label>

          <input
            type="text"
            v-model="config.nom"
            class="form-control"
            :class="{ 'is-invalid': error.nom }"
          />

          <div class="invalid-feedback">
            {{ error.nom }}
          </div>

        </div>

        <!-- TELEPHONE -->
        <div class="col-md-6">

          <label class="form-label fw-semibold">Contact médical</label>

          <input
            type="text"
            v-model="config.telephone"
            class="form-control"
            :class="{ 'is-invalid': error.telephone }"
          />

          <div class="invalid-feedback">
            {{ error.telephone }}
          </div>

        </div>

        <!-- VILLE -->
        <div class="col-md-12">

          <label class="form-label fw-semibold">Ville</label>

          <input
            type="text"
            v-model="config.ville"
            class="form-control"
            :class="{ 'is-invalid': error.ville }"
          />

          <div class="invalid-feedback">
            {{ error.ville }}
          </div>

        </div>

        <!-- ADRESSE -->
        <div class="col-md-12">

          <label class="form-label fw-semibold">Structure / Adresse</label>

          <input
            type="text"
            v-model="config.adresse"
            class="form-control"
            :class="{ 'is-invalid': error.adresse }"
          />

          <div class="invalid-feedback">
            {{ error.adresse }}
          </div>

        </div>

        <!-- DESCRIPTION -->
        <div class="col-md-12">

          <label class="form-label fw-semibold">Description</label>

          <textarea
            v-model="config.description"
            class="form-control"
            rows="3"
          ></textarea>

        </div>

        <!-- LOGO -->
        <div class="col-md-6">

          <label class="form-label fw-semibold">
            Logo du système médical
          </label>

          <input
            type="file"
            @change="handleFileUpload"
            class="form-control"
            accept="image/*"
          />

          <div v-if="config.logo" class="mt-2">
            <img
              :src="getLogoUrl(config.logo)"
              class="img-thumbnail logo-preview"
            />
          </div>

        </div>

      </div>

      <!-- ACTIONS -->
      <div class="d-flex justify-content-end gap-3 mt-4">

        <button
          class="btn btn-light border shadow-sm px-4 py-2"
          @click="cancel"
        >
          Annuler
        </button>

        <button
          class="btn btn-primary btn-medical px-4 py-2"
          :disabled="isSaving"
          @click="updateConfiguration"
        >
          <span
            v-if="isSaving"
            class="spinner-border spinner-border-sm me-2"
          ></span>

          <span v-if="isSaving">Mise à jour...</span>
          <span v-else>Mettre à jour</span>
        </button>

      </div>

    </div>

  </div>
</template>

<script>
import api from '@/api';

export default {
  data() {
    return {

      isPageLoading: true,
      isSaving: false,

      config: {
        id: null,
        nom: '',
        adresse: '',
        ville: '',
        telephone: '',
        description: '',
        logo: ''
      },

      logoFile: null,

      error: {
        nom: '',
        adresse: '',
        ville: '',
        telephone: ''
      }

    };
  },

  methods: {

    async loadConfiguration() {
      this.isPageLoading = true;

      try {
        const id = this.$route.params.id;
        const response = await api.getItem('configurations', id);

        this.config = response.data;

      } catch (err) {
        console.error(err);
        alert("Impossible de charger la configuration");
      } finally {
        this.isPageLoading = false;
      }
    },

    handleFileUpload(event) {
      this.logoFile = event.target.files[0];
    },

    getLogoUrl(path) {
      return path
        ? `${process.env.VUE_APP_API_BASE_URL}${path}`
        : '';
    },

    validateForm() {

      this.error = {
        nom: this.config.nom ? '' : 'Le nom est requis',
        ville: this.config.ville ? '' : 'La ville est requise',
        adresse: this.config.adresse ? '' : "L'adresse est requise",
        telephone: this.config.telephone ? '' : 'Le contact est requis'
      };

      return !Object.values(this.error).some(e => e !== '');
    },

    async updateConfiguration() {

      if (!this.validateForm() || this.isSaving) return;

      try {

        this.isSaving = true;

        let payload;
        let isMultipart = false;

        if (this.logoFile) {

          payload = new FormData();

          payload.append('nom', this.config.nom);
          payload.append('ville', this.config.ville);
          payload.append('adresse', this.config.adresse);
          payload.append('telephone', this.config.telephone);
          payload.append('description', this.config.description);
          payload.append('logoFile', this.logoFile);

          isMultipart = true;

        } else {

          payload = {
            nom: this.config.nom,
            ville: this.config.ville,
            adresse: this.config.adresse,
            telephone: this.config.telephone,
            description: this.config.description
          };

        }

        const response = await api.updateItem(
          'configurations',
          this.config.id,
          payload,
          isMultipart
        );

        this.config = response.data;
        this.logoFile = null;

        this.$router.push({ name: 'item_config' });

      } catch (error) {
        console.error(error);
        alert("Erreur lors de la mise à jour");
      } finally {
        this.isSaving = false;
      }
    },

    cancel() {
      this.$router.push({ name: 'item_config' });
    }

  },

  created() {
    this.loadConfiguration();
  }

};
</script>
<style scoped>

/* LOADER */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* HEADER MEDICAL */
.header-medical {
  padding: 15px 20px;
  background: linear-gradient(135deg, #0f4c81, #1e88e5);
  border-radius: 14px;
  color: white;
}

/* TEXT */
.text-header {
  color: rgba(255,255,255,0.75);
}

/* CARD */
.card-medical {
  background: #f8fbff;
  border-radius: 14px;
  border: none;
}

/* INPUT */
.form-control {
  border-radius: 10px;
  border: 1px solid #d1e3f0;
  padding: 10px 14px;
}

.form-control:focus {
  border-color: #1e88e5;
  box-shadow: 0 0 6px rgba(30,136,229,0.2);
}

/* BUTTON */
.btn-medical {
  background: linear-gradient(135deg,#0f4c81,#1e88e5);
  border: none;
  color: white;
  border-radius: 10px;
}

/* TITLE */
.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #0f4c81;
}

/* LOGO */
.logo-preview {
  max-height: 100px;
  border-radius: 10px;
}

</style>